import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ScrollToFragmentDirectiveModule } from '@commons/directives/scroll-to-fragment/scroll-to-fragment.directive';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { Section, SectionIdEnum, SECTIONS } from '@features/homeB/sections';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';

export enum NavbarPosition {
  mobile = 'mobile',
  top = 'top',
  bottom = 'bottom',
  hidden = 'hidden',
  fixed = 'fixed',
}

type CursorStyleType = {
  left: string;
  width: string;
};

@UntilDestroy()
@Component({
  selector: 'app-navbar-section-home-b',
  templateUrl: './navbar-section-home-b.component.html',
  styleUrls: ['./navbar-section-home-b.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarSectionHomeBComponent implements OnInit, OnDestroy {
  @Input() set initIndex(initIndex: number) {
    this.currentIndex = initIndex || 0;
    const section = this.SECTIONS[initIndex || 0];
    this.accentColor = section.accentColor;
    this.currentNavigation$.next(section.id);
    this.cdr.detectChanges();
    this.isMobile$.pipe(take(1)).subscribe({
      next: (isMobile) => {
        this.cursorStyle$.next(this.updadeCursor(this.currentIndex, isMobile));
      },
    });
  }
  @Output() navigationItemClicked = new EventEmitter<string>();

  @ViewChild('listSections') container!: ElementRef;

  SECTIONS = SECTIONS;

  @Input()
  set currentNavigation(itemValue: string | undefined) {
    if (itemValue) {
      this.currentNavigation$.next(itemValue);
    }
  }

  @Input()
  set navPosition(navState: NavbarPosition) {
    this.navFixedState$.next(navState);
  }

  currentNavigation$ = new BehaviorSubject<string>(SectionIdEnum.jobs);
  cursorStyle$ = new BehaviorSubject<CursorStyleType | undefined>(undefined);
  navFixedState$ = new BehaviorSubject<NavbarPosition>(NavbarPosition.top);
  NavbarPosition = NavbarPosition;

  vm$: Observable<{
    currentNav: string;
    isMobile: boolean;
    navFixedState: NavbarPosition;
    cursorStyle: CursorStyleType | undefined;
  }> = combineLatest([this.currentNavigation$, this.isMobile$, this.navFixedState$, this.cursorStyle$]).pipe(
    distinctUntilChanged(),
    map(([currentNav, isMobile, navFixedState, cursorStyle]) => ({
      currentNav,
      isMobile,
      navFixedState,
      cursorStyle,
    }))
  );
  accentColor = '#526ab6';

  total = SECTIONS.length;

  currentIndex = 0;

  @HostBinding('attr.style')
  public get valueAsStyle(): any {
    return this.sanitizer.bypassSecurityTrustStyle(`--accent-color: ${this.accentColor}`);
  }

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobileOrTablet$;
  }

  get positionClass$(): Observable<string> {
    return combineLatest([this.navFixedState$, this.isMobile$]).pipe(
      map(([position, isMobile]) => {
        const className = 'section-navbar';
        if (isMobile) {
          return position === NavbarPosition.mobile ? `${className}--fixed-mobile` : `${className}--hidden`;
        }
        if (position === NavbarPosition.fixed) return `${className}--fixed`;
        if (position === NavbarPosition.bottom) return `${className}--fixed-bottom`;
        return '';
      })
    );
  }

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: any
  ) {}

  ngOnInit(): void {
    this.cdr.detectChanges();

    this.isMobile$.pipe(untilDestroyed(this)).subscribe((v) => {
      this.cursorStyle$.next(this.updadeCursor(this.currentIndex, v));
    });
  }

  updadeCursor(i: number, isMobile: boolean): CursorStyleType {
    const rect = this.container?.nativeElement.getBoundingClientRect();

    const child = this.document.getElementById(`nav-item-${i}`).getBoundingClientRect();
    this.currentIndex = i;

    if (isMobile) {
      return { left: `${child.x - rect.x + child.width / 2 - 14}px`, width: '28px' };
    }
    return { left: `${child.x - rect.x - 20}px`, width: `${child.width + 40}px` };
  }

  click(section: Section): void {
    this.navigationItemClicked.emit(section.id);
  }

  trackBySection(_: number, section: Section): string {
    return section.id;
  }

  ngOnDestroy(): void {
    this.currentNavigation$.complete();
    this.cursorStyle$.complete();
  }
}

@NgModule({
  imports: [CommonModule, MatIconModule, ScrollToFragmentDirectiveModule],
  declarations: [NavbarSectionHomeBComponent],
  exports: [NavbarSectionHomeBComponent],
})
export class NavbarSectionHomeBModule {}
